import { useEffect, useMemo, useState } from 'react';
import UseAxios from '../../../../Api/http/http';
import { getProjectStatusMaster } from '../../../../Api/request/projectDetails';

export const STATUS_OPTIONS_LOAD_ERROR = 'Unable to load project statuses. Please try again.';

let cachedStatusOptions = null;
let statusOptionsPromise = null;

const normalizeStatusOption = (item) => String(
  item?.project_status_name
  ?? item?.projectStatusName
  ?? item?.project_status
  ?? item?.label
  ?? '',
).trim();

const toUniqueOptions = (options = []) => Array.from(
  new Map(
    options
      .map((option) => String(option || '').trim())
      .filter(Boolean)
      .map((option) => [option.toLowerCase(), option]),
  ).values(),
);

export const buildValidStatusMessage = (options = []) => {
  const validOptions = toUniqueOptions(options);
  if (!validOptions.length) {
    return 'Please select a valid status.';
  }

  return `Please select a valid status: ${validOptions.join(', ')}.`;
};

const fetchProjectStatusOptions = async () => {
  if (Array.isArray(cachedStatusOptions) && cachedStatusOptions.length) {
    return cachedStatusOptions;
  }

  if (!statusOptionsPromise) {
    statusOptionsPromise = UseAxios(getProjectStatusMaster())
      .then((response) => {
        const responseData = response?.data?.data;
        const statusRows = Array.isArray(responseData)
          ? responseData
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const resolvedOptions = toUniqueOptions(statusRows.map(normalizeStatusOption));

        if (!resolvedOptions.length) {
          throw new Error(STATUS_OPTIONS_LOAD_ERROR);
        }

        cachedStatusOptions = resolvedOptions;
        return resolvedOptions;
      })
      .finally(() => {
        statusOptionsPromise = null;
      });
  }

  return statusOptionsPromise;
};

export const primeProjectStatusOptions = () => fetchProjectStatusOptions().catch(() => []);

export const useProjectStatusOptions = ({ isOpen = false, includeValue = '' } = {}) => {
  const [fetchedOptions, setFetchedOptions] = useState(() => (
    Array.isArray(cachedStatusOptions) ? cachedStatusOptions : []
  ));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    let isActive = true;

    const loadProjectStatuses = async () => {
      setIsLoading(true);
      setError('');

      try {
        const resolvedOptions = await fetchProjectStatusOptions();

        if (!isActive) return;

        setFetchedOptions(resolvedOptions);
      } catch (fetchError) {
        if (!isActive) return;
        setFetchedOptions([]);
        setError(STATUS_OPTIONS_LOAD_ERROR);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadProjectStatuses();

    return () => {
      isActive = false;
    };
  }, [isOpen]);

  const statusOptions = useMemo(
    () => toUniqueOptions([...fetchedOptions, includeValue]),
    [fetchedOptions, includeValue],
  );

  return {
    statusOptions,
    isLoading,
    error,
  };
};
